import { useState, useEffect } from "react";
import api from "../../../utils/api";

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    OutlinedInput,
    FormHelperText,
    Button,
    Box,
    FormControl,
    FormLabel,
    CircularProgress,
    Typography,
    FormControlLabel,
    Switch,
} from "@mui/material";

const initial = {
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    is_moderated: true,
};

export default function StudentModal({ open, onClose, row = null }) {
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [form, setForm] = useState(initial);
    const [avatarFile, setAvatarFile] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        try {
            setSaving(true);
            setErrors({});

            if (row?.id) {
                const fd = new FormData();
                fd.append("first_name", form.first_name);
                fd.append("last_name", form.last_name);
                fd.append("email", form.email);
                if (avatarFile) {
                    fd.append("avatar", avatarFile);
                }
                fd.append("is_moderated", form.is_moderated ? "1" : "0");
                await api.post(`admin/students/${row.id}/update`, fd);
            } else {
                const fd = new FormData();
                fd.append("first_name", form.first_name);
                fd.append("last_name", form.last_name);
                fd.append("email", form.email);
                fd.append("password", form.password);
                if (avatarFile) {
                    fd.append("avatar", avatarFile);
                }
                await api.post("admin/students", fd);
            }

            onClose(true);
        } catch (e) {
            if (e.errors) {
                setErrors(e.errors);
            } else if (e.response?.data?.errors) {
                setErrors(e.response.data.errors);
            }
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (row) {
            setForm({
                first_name: row.first_name || "",
                last_name: row.last_name || "",
                email: row.email || "",
                password: "",
                is_moderated: Boolean(row.is_moderated),
            });
        } else {
            setForm(initial);
        }
        setAvatarFile(null);
        setErrors({});
        setSaving(false);
    }, [row, open]);

    return (
        <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
            <DialogTitle>{!row ? "Create Student" : "Edit Student"}</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    <FormControl error={!!errors.first_name?.[0]}>
                        <FormLabel htmlFor="first_name">First Name</FormLabel>
                        <OutlinedInput
                            name="first_name"
                            fullWidth
                            value={form.first_name}
                            onChange={handleChange}
                        />
                        {errors.first_name && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors.first_name}>
                                {errors.first_name[0]}
                            </FormHelperText>
                        )}
                    </FormControl>

                    <FormControl error={!!errors.last_name?.[0]}>
                        <FormLabel htmlFor="last_name">Last Name</FormLabel>
                        <OutlinedInput
                            name="last_name"
                            fullWidth
                            value={form.last_name}
                            onChange={handleChange}
                        />
                        {errors.last_name && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors.last_name}>
                                {errors.last_name[0]}
                            </FormHelperText>
                        )}
                    </FormControl>

                    <FormControl error={!!errors.email?.[0]}>
                        <FormLabel htmlFor="email">Email</FormLabel>
                        <OutlinedInput
                            name="email"
                            type="email"
                            fullWidth
                            value={form.email}
                            onChange={handleChange}
                        />
                        {errors.email && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors.email}>
                                {errors.email[0]}
                            </FormHelperText>
                        )}
                    </FormControl>

                    {row ? (
                        <FormControl>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={Boolean(form.is_moderated)}
                                        onChange={(e) =>
                                            setForm((prev) => ({ ...prev, is_moderated: e.target.checked }))
                                        }
                                    />
                                }
                                label="Moderated"
                            />
                            <FormHelperText sx={{ m: 0.5 }}>
                                Mark when the student account has been reviewed and approved.
                            </FormHelperText>
                        </FormControl>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            New students created by an admin are marked as moderated automatically.
                        </Typography>
                    )}

                    {!row && (
                        <FormControl error={!!errors.password?.[0]}>
                            <FormLabel htmlFor="password">Password</FormLabel>
                            <OutlinedInput
                                name="password"
                                type="password"
                                fullWidth
                                value={form.password}
                                onChange={handleChange}
                            />
                            {errors.password && (
                                <FormHelperText sx={{ m: 0.5 }} error={!!errors.password}>
                                    {errors.password[0]}
                                </FormHelperText>
                            )}
                        </FormControl>
                    )}

                    <FormControl error={!!errors.avatar?.[0]}>
                        <FormLabel htmlFor="student-avatar-input">Avatar (optional)</FormLabel>
                        {row?.avatar && !avatarFile && (
                            <Box sx={{ mb: 1 }}>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                    Current
                                </Typography>
                                <Box
                                    component="img"
                                    src={row.avatar}
                                    alt=""
                                    sx={{ width: 64, height: 64, borderRadius: 1, objectFit: "cover", border: "1px solid", borderColor: "divider" }}
                                />
                            </Box>
                        )}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
                            <input
                                key={row?.id ?? "create"}
                                id="student-avatar-input"
                                name="avatar"
                                type="file"
                                accept="image/jpeg,image/png,image/gif"
                                style={{ display: "none" }}
                                onChange={(e) => setAvatarFile(e.target.files?.[0] ?? null)}
                            />
                            <Button component="label" htmlFor="student-avatar-input" variant="outlined" size="small">
                                {row ? "Replace image" : "Choose image"}
                            </Button>
                            {avatarFile ? (
                                <Typography variant="body2" color="text.secondary">
                                    {avatarFile.name}
                                </Typography>
                            ) : (
                                <Typography variant="caption" color="text.secondary">
                                    JPEG, PNG, or GIF. Max 2 MB.
                                </Typography>
                            )}
                        </Box>
                        {errors.avatar && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors.avatar}>
                                {errors.avatar[0]}
                            </FormHelperText>
                        )}
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()}>Cancel</Button>
                <Button
                    sx={{ color: '#fff' }}
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={saving || (!row && !form.password)}
                >
                    {saving ? <CircularProgress size={24} /> : (!row ? "Create" : "Update")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
